/** SUNTRONIX ONE — Commercial Pipeline Overview.
 *
 * Route: /commercial/overview
 * Live pipeline totals + drill-down into every underlying record.
 * All values are computed on real records — never fabricated KPIs.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Activity, RefreshCw, ChevronLeft, ArrowRight, FileText, Receipt,
  ShieldCheck, ClipboardList, ShoppingCart, Truck, Landmark,
} from "lucide-react";

const fmtINR = (n) => n == null ? "—" :
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR",
    maximumFractionDigits: 0 }).format(n);

const STAGE_META = {
  QUOTATIONS_CREATED:      { label: "Quotations Created",        icon: FileText,      link: "/commercial/crm/quotations" },
  QUOTATIONS_SUBMITTED:  { label: "Quotations Submitted",   icon: FileText,      link: "/commercial/crm/quotations" },
  QUOTATIONS_ACCEPTED:  { label: "Quotations Accepted",     icon: FileText,      link: "/commercial/crm/quotations" },
  CUSTOMER_PO_PENDING:  { label: "Customer PO Pending",     icon: Receipt,      link: "/commercial/customer-po" },
  CUSTOMER_PO_RECEIVED: { label: "Customer PO Received", icon: Receipt,      link: "/commercial/customer-po" },
  PO_VALIDATION_PENDING: { label: "PO Validation Pending", icon: ShieldCheck,  link: "/commercial/customer-po" },
  PO_MISMATCH:                        { label: "PO Mismatch",                     icon: ShieldCheck,  link: "/commercial/customer-po", danger: true },
  ACCEPTED_CUSTOMER_PO: { label: "Accepted Customer PO",  icon: Receipt,      link: "/commercial/customer-po" },
  ORDERS_CREATED:                { label: "Orders Created",                icon: ClipboardList, link: "/commercial/one-orders" },
};

const LIFECYCLE_ICONS = {
  QUOTE:          FileText,
  CUSTOMER_PO: Receipt,
  VALIDATION:  ShieldCheck,
  ORDER:           ClipboardList,
  PROCUREMENT: ShoppingCart,
  DEPLOYMENT:  Truck,
  BILLING:         Landmark,
};

export default function CommercialOverviewPage() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [drilldown, setDrilldown] = useState(null);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/commercial/pipeline/overview");
      setData(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const values = data?.commercial_values || {};

  return (
    <div className="space-y-4 max-w-[1500px]" data-testid="commercial-overview-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <Link to="/commercial/crm" className="text-[11px] text-emerald-700 hover:underline flex items-center gap-1">
            <ChevronLeft className="h-3 w-3" /> Commercial Workspace
          </Link>
          <h1 className="text-2xl font-heading font-semibold text-slate-900 mt-1 flex items-center gap-2">
            <Activity className="h-6 w-6 text-emerald-700" />
            Commercial Pipeline — Overview
          </h1>
          <div className="text-[11px] text-slate-500 mt-1">
            Live counts from real Quotations, Customer POs, and ONE Orders.
            Quoted values are NOT revenue.
          </div>
        </div>
        <Button variant="outline" onClick={load} disabled={busy}
          data-testid="overview-refresh">
          <RefreshCw className={"h-4 w-4 mr-1.5 " + (busy ? "animate-spin" : "")} /> Refresh
        </Button>
      </div>

      {/* Commercial values band */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="overview-values">
        <ValueTile label="Total Quoted Value" value={fmtINR(values.total_quoted_value)}
          hint="Sum of every non-cancelled quotation. NOT revenue." tone="info"
          tid="value-total-quoted" />
        <ValueTile label="Accepted Quoted Value" value={fmtINR(values.accepted_quoted_value)}
          hint="Quotes marked ACCEPTED by the customer. NOT revenue." tone="info"
          tid="value-accepted-quoted" />
        <ValueTile label="Customer PO Value" value={fmtINR(values.customer_po_value)}
          hint="Sum of open Customer POs (excludes cancelled / rejected)." tone="ok"
          tid="value-customer-po" />
        <ValueTile label="Confirmed Order Value" value={fmtINR(values.confirmed_order_value)}
          hint="Sum of ONE Orders in play. Not billed revenue." tone="ok"
          tid="value-confirmed-order" />
      </div>

      {/* Stage tiles */}
      <div>
        <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">
          Pipeline stages · click any number to drill down
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-3" data-testid="overview-stages">
          {(data?.stage_keys || []).map((s) => {
            const m = STAGE_META[s] || { label: s, icon: FileText };
            const st = data?.stages?.[s] || { count: 0, value: 0 };
            return (
              <button key={s} onClick={() => setDrilldown(s)}
                data-testid={`stage-${s}`}
                className={"text-left border rounded-md p-3 hover:shadow " + (
                  m.danger && st.count > 0
                    ? "border-rose-300 bg-rose-50"
                    : "border-slate-200 bg-white"
                )}>
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-slate-600">
                  <m.icon className="h-3.5 w-3.5" /> {m.label}
                </div>
                <div className="flex items-baseline gap-3 mt-1">
                  <span className="text-3xl font-heading font-semibold">{st.count}</span>
                  <span className="text-[11px] text-slate-500 font-mono">{fmtINR(st.value)}</span>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Lifecycle visualisation */}
      <div>
        <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">
          Lifecycle
        </div>
        <div className="flex flex-wrap gap-2 items-stretch" data-testid="overview-lifecycle">
          {(data?.lifecycle_steps || []).map((step, i) => {
            const Icon = LIFECYCLE_ICONS[step.key] || FileText;
            const count = data?.lifecycle?.find((x) => x.key === step.key)?.count || 0;
            return (
              <div key={step.key} className="flex items-center">
                <div className="border border-slate-200 bg-white rounded p-2 text-center min-w-[110px]">
                  <Icon className="h-4 w-4 text-emerald-700 mx-auto" />
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 mt-1">
                    {step.label}
                  </div>
                  <div className="text-lg font-heading font-semibold">{count}</div>
                </div>
                {i < (data?.lifecycle_steps || []).length - 1 && (
                  <ArrowRight className="h-4 w-4 text-slate-400 mx-1" />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {drilldown && (
        <DrillDownDrawer stage={drilldown} onClose={() => setDrilldown(null)} />
      )}
    </div>
  );
}

function ValueTile({ label, value, hint, tone, tid }) {
  const tint = tone === "ok" ? "bg-emerald-50 border-emerald-200 text-emerald-900"
             : tone === "info" ? "bg-slate-50 border-slate-200 text-slate-800"
             : "bg-white border-slate-200 text-slate-800";
  return (
    <div className={"border rounded p-3 " + tint} data-testid={tid}>
      <div className="text-[10px] uppercase tracking-wider">{label}</div>
      <div className="text-2xl font-heading font-semibold mt-1 font-mono">{value}</div>
      <div className="text-[10px] text-slate-500 mt-1 italic">{hint}</div>
    </div>
  );
}

function DrillDownDrawer({ stage, onClose }) {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(true);
  const [kind, setKind] = useState(null);
  useEffect(() => {
    setBusy(true);
    api.get(`/commercial/pipeline/records?stage=${stage}`)
      .then(({ data }) => { setItems(data.items || []); setKind(data.kind); })
      .catch((e) => toast.error(e.response?.data?.detail || "Failed"))
      .finally(() => setBusy(false));
  }, [stage]);
  const meta = STAGE_META[stage] || {};
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex justify-end" onClick={onClose}
      data-testid="overview-drilldown">
      <div onClick={(e) => e.stopPropagation()}
        className="w-full max-w-3xl bg-white h-full overflow-auto p-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500">
              Drill-down
            </div>
            <h2 className="text-2xl font-heading font-semibold mt-1">
              {meta.label || stage}
            </h2>
            <div className="text-[11px] text-slate-500 mt-1">
              {items.length} record(s) · showing latest 200
            </div>
          </div>
          <button onClick={onClose} className="text-slate-500 text-[13px]">Close ✕</button>
        </div>
        <div className="mt-4">
          {busy && <div className="text-center text-slate-500 text-sm py-8">Loading…</div>}
          {!busy && items.length === 0 && (
            <div className="p-8 text-center text-[13px] text-slate-500">
              No records in this stage right now.
            </div>
          )}
          {!busy && items.length > 0 && (
            <div className="border border-slate-200 rounded divide-y">
              {items.map((r) => (
                <div key={r.id} className="p-3 flex items-center gap-3"
                  data-testid={`drilldown-row-${r.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-[13px] font-semibold">{r.code}</span>
                      {r.status && (
                        <Badge variant="outline" className="text-[10px]">
                          {String(r.status).replace(/_/g, " ")}
                        </Badge>
                      )}
                      {r.acceptance_status && (
                        <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200">
                          {r.acceptance_status}
                        </Badge>
                      )}
                      {r.last_validation_status && (
                        <Badge variant="outline" className="text-[10px]">
                          {String(r.last_validation_status).replace(/_/g, " ")}
                        </Badge>
                      )}
                    </div>
                    <div className="text-[12px] text-slate-700 mt-0.5 truncate">
                      {r.customer_name || "—"}
                      {r.customer_po_number && <span className="text-slate-500 ml-2">· PO {r.customer_po_number}</span>}
                      {r.quotation_code && <span className="text-slate-500 ml-2">· QUO {r.quotation_code}</span>}
                      {r.subject && <span className="text-slate-500 ml-2">· {r.subject}</span>}
                    </div>
                    <div className="text-[10px] text-slate-500 mt-0.5">
                      {r.created_at && new Date(r.created_at).toLocaleString()}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] uppercase text-slate-500">Value</div>
                    <div className="text-[14px] font-heading font-semibold font-mono">
                      {fmtINR(r.grand_total_inr ?? r.total_amount_inr)}
                    </div>
                  </div>
                  {meta.link && (
                    <Link to={meta.link}
                      className="text-[12px] text-emerald-700 hover:underline">
                      Open →
                    </Link>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
